<template>
  <div class="page-company-user">
    <PageHeader>
      <div class="heading">
        <h1>
          {{ company.name }} /
          <router-link
            :to="{name: 'company-users', params: {companyId: companyId}}"
          >
            My Team
          </router-link>
          / <strong class="name">{{ companyUser.name}}</strong>
        </h1>
      </div>

      <div class="header-actions">
        <Permissions
          v-if="!isCurrentUser"
          :permissions="currentUser.permissions"
          :requireAll="{company_user: ['delete']}"
        >
          <Btn secondary @click="activateConfirm">
            Delete User
          </Btn>
        </Permissions>

        <div v-else>
          <Btn subtle v-if="loggedIn" @click="logout">
            Logout
          </Btn>
        </div>
      </div>
    </PageHeader>

    <div class="page-columns">
      <div class="col main">
        <div class="page-content">
          <Notification
            v-if="notification.active"
            :content="notification.content"
            :type="notification.type"
          />

          <section class="account-actions">
            <div class="account-action">
              <h3 class="subtle">Profile</h3>
              <p>
                <strong>Full Name:</strong> {{ companyUser.name }}<br>
                <strong>Login Email:</strong> {{ companyUser.email }}
              </p>

              <div class="centered">
                <div v-if="isCurrentUser || isAdmin">
                  <router-link
                    class="btn is-subtle"
                    :to="{
                      name: 'edit-company-user',
                      params: {companyId: companyId, userId: userId}
                    }"
                  >
                    Edit My Profile
                  </router-link>
                </div>

                <div v-else>
                  <Permissions
                    :permissions="currentUser.permissions"
                    :requireAll="{company_user: ['update']}"
                  >
                    <router-link
                      class="btn is-subtle"
                      :to="{
                        name: 'edit-company-user',
                        params: {companyId: companyId, userId: userId}
                      }"
                      >
                        Edit User Profile
                    </router-link>
                  </Permissions>
                </div>
              </div>
            </div>

            <div class="account-action">
              <h3 class="subtle">Company</h3>
              <p>
                <strong>Company:</strong> {{ company.name }}
                <span v-if="companyUser.owns_company">(company owner)</span>
                <br>
                <strong>Contact Form Email:</strong> {{ company.email }}
              </p>

              <div class="centered">
                <router-link
                  class="btn is-subtle"
                  :to="{
                    name: 'edit-company',
                    params: {companyId: companyUser.company_id}
                  }"
                >
                  Edit Company
                </router-link>
              </div>
            </div>
          </section>

          <section v-if="isCurrentUser || isAdmin">
            <div class="account-actions">
              <div class="account-action">
                <h3 class="subtle">Change Your Password</h3>
                <transition name="fade" mode="out-in">
                  <p key="password-changed" v-if="resettingPassword">
                    We've just sent you an email. Please follow the link to reset your password.
                  </p>

                  <div key="password-init" v-else>
                    <p>To change your password, we'll send a link to your email address, <strong>{{companyUser.email}}</strong>.</p>

                    <div class="centered">
                      <Btn
                        subtle
                        :isLoading="passwordBtnLoading"
                        @click="resetPassword">
                        Request Change
                      </Btn>
                    </div>
                  </div>
                </transition>
              </div>

              <div class="account-action">
                <h3 class="subtle">Change Your Email Address</h3>
                <p>You'll need to confirm this change by clicking a link that will be sent to your new email address.</p>

                <EmailForm
                  @submit="submitEmail"
                  v-model="user"
                  :errors="emailErrors" />
              </div>
            </div>
          </section>
        </div>
      </div>

      <div class="col side">
        <PageSummary>
          <div>
            <div class="user-action">
              <h1>User</h1>

              <Permissions
                :permissions="currentUser.permissions"
                :requireAll="{company_user: ['create']}"
              >
                <router-link
                  class="btn is-tertiary is-small"
                  :to="{name: 'add-company-user'}"
                  >Invite user</router-link
                >
              </Permissions>
            </div>

            <p>
              This is the profile page of
              <strong>{{ companyUser.email }}</strong
              >, who has access to <strong>{{ company.name }}</strong
              >. This account is currently
              <strong v-if="companyUser.active">active</strong
              ><strong v-else>inactive</strong>.
            </p>
          </div>
        </PageSummary>
      </div>
    </div>

    <router-view />

    <Confirm
      v-if="confirmActive"
      @confirm="deleteCompanyUser"
      @cancel="cancel"
      :message="deleteMessage"
      :link="{
        name: 'websites',
        params: {companyId: companyId}
      }"
    />
  </div>
</template>

<script>
import {mapState, mapActions, mapGetters} from 'vuex'
import page from '@/mixins/page'
import pagination from '@/mixins/pagination'
import confirm from '@/mixins/confirm'
import notification from '@/mixins/notification'

import {readCompanyData} from '@/lib/companies'

import {
  readCompanyUserData,
  updateCompanyUserData,
  deleteCompanyUserData
} from '@/lib/company_users'

import {initResetPassword} from '@/lib/users'

import {initUserEmailReset} from '@/lib/email_resets'

import PageHeader from '@/components/PageHeader'
import PageSummary from '@/components/PageSummary'
import EmailForm from '@/components/forms/EmailForm'
import Confirm from '@/components/Confirm'
import Permissions from '@/components/Permissions'
import Btn from '@/components/Btn'

export default {
  name: 'CompanyUser',

  mixins: [page, pagination, confirm, notification],

  props: {
    companyId: {
      type: String,
      required: true
    },

    userId: {
      type: String,
      required: true
    }
  },

  components: {
    PageHeader,
    PageSummary,
    Confirm,
    EmailForm,
    Permissions,
    Btn
  },

  data() {
    return {
      passwordBtnLoading: false,
      resettingPassword: false,
      company: {},
      companyUser: {},
      uuser: {permissions: {}},
      errors: {},
      emailErrors: {}
    }
  },

  methods: {
    ...mapActions(['logoutUser']),

    logout() {
      this.logoutUser(this.session)
        .then(() => {
          this.$router.push({name: 'login'})
        })
        .catch(() => {
          alert('Error logging out')
        })
    },

    resetPassword() {
      this.passwordBtnLoading = true

      let user = {email: this.companyUser.email}

      initResetPassword(user)
        .then(() => {
          this.triggerNotification('success', 'reset email', 'sent')

          window.setTimeout(() => {
            this.resettingPassword = true
            this.passwordBtnLoading = false
          }, this.closeDelay)
        })
        .catch(({response}) => {
          this.triggerNotification('unsuccessful', 'reset', 'email not found')
          this.errors = response.errors
          this.passwordBtnLoading = false
        })
    },

    getData() {
      readCompanyData(this.companyId).then(({data}) => {
        this.company = data
      })

      readCompanyUserData(this.companyId, this.userId).then(({data}) => {
        this.companyUser = data
      })
    },

    submitCompanyUser() {
      updateCompanyUserData(this.companyId, this.userId, this.companyUser).then(
        ({data}) => {
          this.companyUser = data
        }
      )
    },

    deleteCompanyUser() {
      deleteCompanyUserData(this.companyId, this.userId).then(() => {
        const companyId = this.companyId

        this.confirmActive = false

        this.triggerNotification('success', 'user', 'deleted')

        window.setTimeout(() => {
          this.$router.push({name: 'websites', params: {companyId}})
        }, this.closeDelay)
      })
    },

    submitEmail() {
      initUserEmailReset(this.userId, this.user).then(({data}) => {
        this.triggerNotification('success', 'reset email', 'sent')
      })
    }
  },

  computed: {
    ...mapState(['currentUser', 'session']),
    ...mapGetters(['loggedIn']),

    emailReset() {
      return this.$route.query.emailReset
    },

    user: {
      get() {
        // set empty array for each available permission relm so that vue
        // witchcraft kicks in and we can populate permission relm actions via
        // checkboxes.
        let user = this.uuser

        Object.keys(this.availablePermissions).forEach((relm) => {
          if (!user.permissions[relm]) {
            user.permissions[relm] = []
          }
        })

        return user
      },

      set(value) {
        this.uuser = value
      }
    },

    availablePermissions() {
      return this.currentUser.permissions
    },

    title() {
      return `${this.companyUser.email} - ${this.company.name}`
    },

    isCurrentUser() {
      return this.currentUser.id === this.companyUser.id
    },

    isAdmin() {
      return this.currentUser.admin
    },

    deleteMessage() {
      return 'Are you sure you want to delete this user?'
    },

    backLink() {
      return {
        name: 'websites',
        params: {
          companyId: this.companyId
        }
      }
    }
  },

  mounted() {
    this.getData()

    if (this.emailReset) {
      this.triggerNotification('success', 'email', 'updated')
    }
  },

  watch: {
    $route() {
      this.getData()
    }
  }
}
</script>

<style lang="sass">
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'

.page-company-user
  @extend %page

.active-state
  @extend %active-state

.account-actions
  display: flex

  .account-action
    @extend %card
    flex-basis: 50%
    position: relative
    margin: 0 $gap-small $gap-medium
</style>
